import { FC } from "react";
import { Box, Theme } from "@mui/material";
import { getContrastText } from "../../../../utils/colorUtils";

/**
 * The Visit Result chip, shared by Visit Detail and the Disbursed Incentives
 * drill-down — SIMS-9012.
 *
 * It lived inside `VisitDetailTab` and the drill-down grew its own version: one
 * neutral pill for every result, no colour at all. The same visit therefore
 * looked like two different things depending on which screen you were on, one
 * click apart. Lifted here rather than copied, because two implementations of
 * the same chip is exactly how the two screens drifted in the first place.
 *
 * ## Where the colour comes from, and why it matters
 *
 * The fill is the admin-configured `EventResult.displayColor`, surfaced as
 * `resultColor`. It is deliberately NOT derived from the label.
 *
 * Visit Detail's chips turned grey once (SIMS-7770) for precisely that reason:
 * the colour was matched on the label text, and the label changed from the full
 * display ("UDS - Stimulant Negative ($)", which contained "negative") to the
 * configured shortName ("Neg.", which matches nothing). A label is
 * presentational and must not drive colour.
 *
 * The keyword fallback below runs only when there is no configured colour —
 * rows the backend emits as literal labels ("Invalid" for tampered, "Missed"
 * for flag-driven), a result whose configuration sets no colour, and accounts
 * that store no event on the visit at all, which is the case on the account the
 * Disbursed Incentives report was built against.
 */

/** Fallback palette, keyed off the label. Only used when no colour is configured. */
export const resultStyle = (
	value: string | null,
	theme: Theme,
	neutral: { bg: string; color: string },
): { bg: string; color: string } => {
	const s = (value ?? "").toLowerCase();
	// SIMS-7563: Positive/Unexcused → red
	if (/(unexcused|positive)/.test(s)) {
		return { bg: theme.palette.error.light, color: theme.palette.error.dark };
	}
	// Excused absence → amber
	if (/excus/.test(s)) {
		return {
			bg: theme.palette.warning.light,
			color: theme.palette.warning.dark,
		};
	}
	// Missed visit → blue
	if (/missed/.test(s)) {
		return { bg: theme.palette.info.light, color: theme.palette.info.dark };
	}
	// Invalid → neutral
	if (/invalid/.test(s)) {
		return neutral;
	}
	// Negative/Attended → teal. Note the domain inversion: Negative is the GOOD
	// outcome here, which is why it is not red.
	if (/(negative|attend)/.test(s)) {
		return {
			bg: theme.palette.secondary.light,
			color: theme.palette.secondary.dark,
		};
	}
	return neutral;
};

export const ResultPill: FC<{
	value: string | null;
	/** The admin-configured colour, when the result has one. */
	color?: string | null;
	theme: Theme;
	/** The tab's own neutral tokens, for the unmatched and Invalid cases. */
	neutral: { bg: string; color: string };
	/** What an absent result renders as — the two tabs differ here. */
	absent?: string;
}> = ({ value, color, theme, neutral, absent = "—" }) => {
	if (!value) return <span>{absent}</span>;
	// A configured colour is used as the fill; the text colour is then picked by
	// WCAG relative luminance, because admins configure anything from pastels
	// (#cce8b5) to fully saturated hues (#e61e1e).
	const st = color
		? { bg: color, color: getContrastText(color) }
		: resultStyle(value, theme, neutral);
	return (
		<Box
			component="span"
			sx={{
				display: "inline-block",
				bgcolor: st.bg,
				color: st.color,
				fontSize: 13,
				fontWeight: 600,
				px: 1.5,
				py: 0.5,
				borderRadius: "9999px",
			}}
		>
			{value}
		</Box>
	);
};
